import L from 'leaflet';
import { pointOnFeature } from '@turf/turf';

import { normalizeStatus } from '../../../config/layerDictionary.js';
import { canViewPendingReviewPins } from '../../../utils/auth.js';

const PENDING_REVIEW_STATUS = 'Aprovado com Pendência';
const REVIEW_COMPLETED = 'CONCLUIDA';

const createPinIcon = (fill, stroke) =>
	L.divIcon({
		className: '',
		html: `
			<svg
				width="18"
				height="25"
				viewBox="0 0 30 42"
				xmlns="http://www.w3.org/2000/svg"
				aria-hidden="true"
			>
				<path
					d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 27 15 27s15-15.75 15-27C30 6.72 23.28 0 15 0Z"
					fill="${fill}"
					stroke="${stroke}"
					stroke-width="1.5"
				/>
				<circle
					cx="15"
					cy="15"
					r="5.5"
					fill="#FFFFFF"
				/>
			</svg>
		`,
		iconSize: [20, 25],
		iconAnchor: [9, 20],
		popupAnchor: [0, -23],
	});

const pendingReviewIcon = createPinIcon('#D71920', '#8B0000');
const completedReviewIcon = createPinIcon('#F4C430', '#9A7600');

const normalizeReviewStatus = value =>
	String(value ?? '')
		.trim()
		.toUpperCase();

const getPinIcon = status =>
	normalizeReviewStatus(status) === REVIEW_COMPLETED
		? completedReviewIcon
		: pendingReviewIcon;

const getPinTitle = status =>
	normalizeReviewStatus(status) === REVIEW_COMPLETED
		? 'Revisão concluída - abrir relatório'
		: 'Abrir revisão do produto';

const isPolygonFeature = feature => {
	const geometryType = feature?.geometry?.type;
	return geometryType === 'Polygon' || geometryType === 'MultiPolygon';
};

export const shouldShowPendingReviewPin = feature => {
	if (!canViewPendingReviewPins()) return false;
	if (!isPolygonFeature(feature)) return false;

	return (
		normalizeStatus(feature?.properties?.status) === PENDING_REVIEW_STATUS
	);
};

export const setPendingReviewPinStatus = (marker, status) => {
	if (!marker) return;

	const normalizedStatus = normalizeReviewStatus(status);
	marker._pendingReviewStatus = normalizedStatus;
	marker.setIcon(getPinIcon(normalizedStatus));

	const title = getPinTitle(normalizedStatus);
	marker.options.title = title;

	const element = marker.getElement?.();
	if (element) {
		element.setAttribute('title', title);
		element.setAttribute(
			'aria-label',
			normalizedStatus === REVIEW_COMPLETED
				? 'Revisão concluída'
				: 'Revisão pendente'
		);
	}
};

export const addPendingReviewPin = (
	feature,
	parentLayer,
	layerName,
	onPendingReviewOpen
) => {
	if (!shouldShowPendingReviewPin(feature) || !parentLayer) return null;

	try {
		const point = pointOnFeature(feature);
		const [lng, lat] = point.geometry.coordinates;
		const reviewStatus = normalizeReviewStatus(
			feature?.properties?._revisao_status
		);

		const marker = L.marker([lat, lng], {
			icon: getPinIcon(reviewStatus),
			riseOnHover: true,
			title: getPinTitle(reviewStatus),
			alt:
				reviewStatus === REVIEW_COMPLETED
					? 'Revisão de produto concluída'
					: 'Produto aprovado com pendência',
		});

		marker._isPendingReviewPin = true;
		marker._pendingReviewFeature = feature;
		marker._pendingReviewLayerName = layerName;
		marker._pendingReviewStatus = reviewStatus;

		marker.on('click', event => {
			if (event.originalEvent) {
				L.DomEvent.stopPropagation(event.originalEvent);
			}

			if (typeof onPendingReviewOpen === 'function') {
				onPendingReviewOpen({
					feature,
					layerName,
					marker,
				});
			}
		});

		marker.addTo(parentLayer);

		return marker;
	} catch (error) {
		console.error('Erro ao criar PIN de produto com pendência:', error);
		return null;
	}
};
